/**
 * Timezone utility functions
 * Shared between composables and mixins
 */

import { usePage } from '@inertiajs/vue3';

// The only locale literal in the frontend; anything a reader sees uses `useLocale`.
// en-GB, not en-US: getTimezoneAbbreviation gives Copenhagen `CET` rather than `GMT+1`.
const EXTRACTION_LOCALE = 'en-GB';

/**
 * The wall clock showing in a timezone. Month is 1-indexed, unlike `Date`.
 *
 * @returns {{year: number, month: number, day: number, hour: number, minute: number, second: number}}
 */
export function getWallClockInTimezone(date, timezone = null) {
    const parts = new Intl.DateTimeFormat(EXTRACTION_LOCALE, {
        timeZone: timezone || getVendorTimezone(),
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hourCycle: 'h23',
    }).formatToParts(date);

    const partsMap = {};
    parts.forEach((part) => {
        partsMap[part.type] = part.value;
    });

    return {
        year: parseInt(partsMap.year, 10),
        month: parseInt(partsMap.month, 10),
        day: parseInt(partsMap.day, 10),
        hour: parseInt(partsMap.hour, 10),
        minute: parseInt(partsMap.minute, 10),
        second: parseInt(partsMap.second, 10),
    };
}

/**
 * That wall clock as a UTC timestamp. Minus the instant's own timestamp gives the offset.
 */
export function wallClockAsUtcMs(date, timezone = null) {
    const { year, month, day, hour, minute, second } = getWallClockInTimezone(date, timezone);

    return Date.UTC(year, month - 1, day, hour, minute, second);
}

/**
 * `HH:MM`, or `HH:MM:SS` when seconds are asked for.
 */
export function getTimeInTimezone(date, timezone = null, { seconds = false } = {}) {
    const { hour, minute, second } = getWallClockInTimezone(date, timezone);
    const pad = (value) => String(value).padStart(2, '0');

    return seconds ? `${pad(hour)}:${pad(minute)}:${pad(second)}` : `${pad(hour)}:${pad(minute)}`;
}

/**
 * The zone's short name — `CET`, `GMT+1`. Empty when it has none.
 */
export function getTimezoneAbbreviation(date, timezone = null) {
    const parts = new Intl.DateTimeFormat(EXTRACTION_LOCALE, {
        timeZone: timezone || getVendorTimezone(),
        timeZoneName: 'short',
    }).formatToParts(date);

    return parts.find((part) => part.type === 'timeZoneName')?.value ?? '';
}

/**
 * Get the timezone offset in hours for a given IANA timezone
 * Accounts for DST automatically based on the provided date
 *
 * @param {string} timezone - IANA timezone identifier (e.g., 'Europe/London')
 * @param {Date|Object} dateInput - Date object or object with y, m, d, h, i properties (month is 0-indexed)
 * @returns {number} - Offset in hours (e.g., 1 for UTC+1)
 */
export function getTimezoneOffset(timezone, dateInput) {
    // Convert dateInput to a UTC-based reference point
    // We use UTC to avoid browser timezone interference
    let utcDate;

    if (dateInput instanceof Date) {
        utcDate = dateInput;
    } else if (dateInput && typeof dateInput === 'object') {
        // Create a UTC date from the components to avoid browser timezone
        utcDate = new Date(Date.UTC(dateInput.y, dateInput.m, dateInput.d, dateInput.h || 0, dateInput.i || 0));
    } else {
        utcDate = new Date();
    }

    const formatter = new Intl.DateTimeFormat(EXTRACTION_LOCALE, {
        timeZone: timezone,
        timeZoneName: 'shortOffset',
    });

    const parts = formatter.formatToParts(utcDate);
    const offsetPart = parts.find((part) => part.type === 'timeZoneName');

    if (!offsetPart) return 0;

    // Parse offset like "GMT+1" or "GMT-5" or "GMT+5:30" or just "GMT"
    const match = offsetPart.value.match(/GMT([+-])?(\d{1,2})?(?::(\d{2}))?/);
    if (!match) return 0;

    // Handle "GMT" (UTC+0) case
    if (!match[1] || !match[2]) return 0;

    const sign = match[1] === '+' ? 1 : -1;
    const hours = parseInt(match[2], 10);
    const minutes = match[3] ? parseInt(match[3], 10) : 0;

    return sign * (hours + minutes / 60);
}

/**
 * Get the vendor timezone from Inertia page props
 * Falls back to 'Europe/Copenhagen' if not available
 *
 * @param {Object} page - Inertia page object from usePage()
 * @returns {string} - IANA timezone identifier
 */
export function getVendorTimezoneFromPage(page) {
    return page?.props?.vendor?.timezone ?? 'Europe/Copenhagen';
}

/**
 * Get the vendor timezone, with fallback for non-Vue contexts
 * This is a convenience wrapper that handles usePage() errors
 *
 * @returns {string} - IANA timezone identifier
 */
export function getVendorTimezone() {
    try {
        const page = usePage();
        return getVendorTimezoneFromPage(page);
    } catch {
        return 'Europe/Copenhagen';
    }
}

/**
 * Format a date (year, month, day) to a string without browser timezone interference
 * This creates date strings directly from components, avoiding Date object timezone issues
 *
 * @param {number} year - Full year (e.g., 2026)
 * @param {number} month - Month (1-12, NOT 0-indexed like JavaScript Date)
 * @param {number} day - Day of month (1-31)
 * @param {string} format - Output format: 'yyyy-MM-dd', 'dd-MM-yyyy', 'dd/MM/yyyy', 'dd-MM-yy', 'dd/MM/yy', 'ddMMyy', 'yyyy-MM'
 * @returns {string} - Formatted date string
 */
export function formatDateComponents(year, month, day, format = 'yyyy-MM-dd') {
    const yearStr = String(year);
    const yearShort = yearStr.slice(-2);
    const monthStr = String(month).padStart(2, '0');
    const dayStr = String(day).padStart(2, '0');

    switch (format) {
        case 'yyyy-MM-dd':
            return `${yearStr}-${monthStr}-${dayStr}`;
        case 'dd-MM-yyyy':
            return `${dayStr}-${monthStr}-${yearStr}`;
        case 'dd/MM/yyyy':
            return `${dayStr}/${monthStr}/${yearStr}`;
        case 'dd-MM-yy':
            return `${dayStr}-${monthStr}-${yearShort}`;
        case 'dd/MM/yy':
            return `${dayStr}/${monthStr}/${yearShort}`;
        case 'ddMMyy':
            return `${dayStr}${monthStr}${yearShort}`;
        case 'yyyy-MM':
            return `${yearStr}-${monthStr}`;
        default:
            return `${yearStr}-${monthStr}-${dayStr}`;
    }
}

/**
 * Parse a date string in yyyy-MM-dd format to components
 * Returns components without creating a Date object to avoid timezone issues
 *
 * @param {string} dateString - Date string in yyyy-MM-dd format
 * @returns {{ year: number, month: number, day: number } | null} - Date components (month is 1-12)
 */
export function parseDateString(dateString) {
    if (!dateString || typeof dateString !== 'string') return null;

    const match = dateString.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!match) return null;

    return {
        year: parseInt(match[1], 10),
        month: parseInt(match[2], 10),
        day: parseInt(match[3], 10),
    };
}

/**
 * Create a UTC Date object for a given date that's safe for comparisons
 * Uses noon UTC to avoid date boundary issues across timezones
 *
 * @param {number} year - Full year
 * @param {number} month - Month (0-indexed like JavaScript Date)
 * @param {number} day - Day of month
 * @returns {Date} - UTC Date object at noon
 */
export function createSafeComparisonDate(year, month, day) {
    return new Date(Date.UTC(year, month, day, 12, 0, 0));
}

/**
 * Create a Date object from year/month/day components at noon
 * This avoids timezone boundary issues when the date is later formatted in a different timezone
 *
 * Use this instead of: new Date(y, m, d)
 *
 * @param {number} year - Full year
 * @param {number} month - Month (0-indexed like JavaScript Date)
 * @param {number} day - Day of month
 * @returns {Date} - Date object at noon local time
 */
export function createDateAtNoon(year, month, day) {
    return new Date(year, month, day, 12, 0, 0);
}

/**
 * Get "today" in vendor timezone as date components
 * Use this instead of: new Date() when you need to know what day it is for the vendor
 *
 * @param {Object} page - Inertia page object (optional, will use usePage() if not provided)
 * @returns {{ year: number, month: number, day: number, date: Date, dateYmd: string }} - Date components (month is 0-indexed)
 */
export function getTodayInVendorTimezone(page = null) {
    const timezone = page ? getVendorTimezoneFromPage(page) : getVendorTimezone();
    const now = new Date();
    const formatter = new Intl.DateTimeFormat(EXTRACTION_LOCALE, {
        timeZone: timezone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    });
    const parts = formatter.formatToParts(now);
    const partsMap = {};
    parts.forEach((part) => {
        partsMap[part.type] = part.value;
    });
    const year = parseInt(partsMap.year, 10);
    const month = parseInt(partsMap.month, 10) - 1; // 0-indexed
    const day = parseInt(partsMap.day, 10);

    return {
        year,
        month,
        day,
        // Create a Date at noon in vendor's perspective for comparisons
        date: createDateAtNoon(year, month, day),
        dateYmd: `${partsMap.year}-${partsMap.month}-${partsMap.day}`,
    };
}

/**
 * Extract date components from a Date object in vendor timezone
 * This is the standalone version of cbDateObject for use outside Vue mixins
 *
 * @param {Date} date - Date object to extract components from
 * @param {string} timezone - IANA timezone (optional, defaults to vendor timezone)
 * @returns {{ y: number, m: number, d: number, date: Date, dateYmd: string }}
 */
export function getDatePartsInTimezone(date, timezone = null) {
    const tz = timezone || getVendorTimezone();
    const formatter = new Intl.DateTimeFormat(EXTRACTION_LOCALE, {
        timeZone: tz,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    });
    const parts = formatter.formatToParts(date);
    const partsMap = {};
    parts.forEach((part) => {
        partsMap[part.type] = part.value;
    });
    return {
        y: parseInt(partsMap.year, 10),
        m: parseInt(partsMap.month, 10) - 1, // 0-indexed month
        d: parseInt(partsMap.day, 10),
        date: date,
        dateYmd: `${partsMap.year}-${partsMap.month}-${partsMap.day}`,
    };
}
